import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import emailIcon from "@iconify/icons-mdi/email";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { LOGIN } from "my-hr/graphql";
import { OcLoading } from "oc/components";
import { useOcClientState } from "oc/hooks";
import useOcProperty from "oc/hooks/use-property";

const validator = new Validator();

type MyHrUnlockFormProps = {
  onSubmit: (loginResult: any) => void;
  buttonLabel: string;
  email: string;
};

export const MyHrUnlockForm: React.FC<MyHrUnlockFormProps> = ({
  onSubmit,
  buttonLabel,
  email,
}) => {
  const { t } = useTranslation();

  const schema = {
    email: {
      type: "email",
      messages: {
        email: t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"),
        emailEmpty: t("OC_ERROR_MESSAGE_EMAIL_IS_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [password, setPassword] = useOcClientState<string>("loginPassword", "");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useOcClientState<boolean>(
    "loginPasswordError",
    false,
  );
  const [passwordErrorText, setPasswordErrorText] = useOcClientState<string>(
    "loginPasswordErrorText",
    "",
  );

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const [login, { loading, data: loginData, error: loginError }] = useLazyQuery(LOGIN, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (loginData?.login) {
      onSubmit(loginData.login);
    }
  }, [loginData, onSubmit]);

  // Timeout: if loading takes more than 10 seconds, reload page
  // This handles hibernation/sleep cases where network is stuck
  useEffect(() => {
    if (loading) {
      const timeoutId = setTimeout(() => {
        window.location.reload();
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  useEffect(() => {
    if (loginError?.graphQLErrors) {
      loginError.graphQLErrors.forEach((item) => {
        if (item.message === "Wrong password!") {
          setPasswordErrorText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
          setPasswordError(true);
        }
      });
    }
  }, [loginError, t, setPasswordErrorText, setPasswordError]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = check({ email, password });

    if (isValidated instanceof Promise) {
      isValidated = await isValidated;
    }

    if (isValidated !== true) {
      isValidated.forEach((item: any) => {
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }

    login({ variables: { email, password } });
  };

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  useEffect(() => {
    return () => {
      setPassword("");
      setPasswordError(false);
      setPasswordErrorText("");
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <OcLoading show={loading} />
      <form noValidate autoComplete="off" onSubmit={handleLogin}>
        <Box padding="12px 0px 22px 0px">
          <TextField
            id="email"
            value={email}
            //onChange={(e) => setEmail(e.target.value)}
            label={t("MY_HR_EMAIL")}
            variant="outlined"
            fullWidth
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InlineIcon height="24px" icon={emailIcon} color="#919699" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box padding="14px 0px 16px 0px">
          <TextField
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={t("MY_HR_PASSWORD")}
            variant="outlined"
            type={showPassword ? "text" : "password"}
            fullWidth
            helperText={passwordErrorText}
            error={passwordError}
            onFocus={() => {
              setPasswordErrorText("");
              setPasswordError(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InlineIcon height="24px" icon={keyIcon} color="#919699" />
                  <Box width="8px" />
                  {passwordVisibilityData === "true" && (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      style={{
                        backgroundColor: "#F1F3F4",
                        height: "48px",
                        width: "48px",
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon
                          style={{ height: "24px", width: "24px" }}
                        />
                      ) : (
                        <VisibilityIcon
                          style={{ height: "24px", width: "24px" }}
                        />
                      )}
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box paddingTop="18px">
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
          >
            {buttonLabel}
          </Button>
        </Box>
      </form>
    </Fragment>
  );
};

export default MyHrUnlockForm;
